import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, BookOpenIcon, CpuChipIcon, UserIcon } from '@heroicons/react/24/outline';

interface UserDetails {
  user: {
    id: string;
    email: string;
    name: string;
    username: string | null;
    emailVerified: boolean;
    isTestUser: boolean;
    isAdmin: boolean;
    isActive: boolean;
    subscriptionPlan: string;
    subscriptionStatus: string | null;
    tokenBalance: number;
    compedPlan: string | null;
    compedAt: string | null;
    createdAt: string;
    lastLoginAt: string | null;
  };
  stats: {
    totalSpent: number;
    totalTokensUsed: number;
    storiesCount: number;
    wordsWritten: number;
    paymentsCount: number;
    aiCalls: number;
  };
  stories: Array<{
    id: string;
    title: string;
    format: string;
    status: string;
    wordCount: number;
    createdAt: string;
    updatedAt: string;
  }>;
  recentAiUsage: Array<{
    id: string;
    model: string;
    type: string;
    totalTokens: number;
    chargedAmount: number;
    createdAt: string;
  }>;
}

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const AdminUserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/admin/users/${userId}`, { headers: authHeaders() });
      setDetails(response.data);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Admin access required');
        navigate('/login');
      } else {
        setError('Failed to load user');
      }
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const updateFlag = async (field: 'isAdmin' | 'isActive' | 'isTestUser', value: boolean) => {
    if (!details) return;
    try {
      setUpdating(true);
      const response = await axios.put(
        `/admin/users/${userId}`,
        { [field]: value },
        { headers: authHeaders() },
      );
      setDetails(response.data);
      toast.success('User updated');
    } catch {
      toast.error('Failed to update user');
    } finally {
      setUpdating(false);
    }
  };

  const setComp = async (give: boolean) => {
    const question = give
      ? 'Give this writer a complimentary Plus plan? Their words refill every month until you end it.'
      : 'End the complimentary plan? They go back to Free, keeping only words they bought.';
    if (!window.confirm(question)) return;
    try {
      setUpdating(true);
      if (give) {
        await axios.post(
          `/admin/users/${userId}/comp`,
          { plan: 'plus' },
          { headers: authHeaders() },
        );
      } else {
        await axios.delete(`/admin/users/${userId}/comp`, { headers: authHeaders() });
      }
      toast.success(give ? 'Complimentary Plus given' : 'Complimentary plan ended');
      await fetchUser();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update the plan');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-red-600">{error || 'User not found'}</p>
      </div>
    );
  }

  const { user, stats, stories, recentAiUsage } = details;

  const Toggle: React.FC<{ label: string; field: 'isAdmin' | 'isActive' | 'isTestUser' }> = ({
    label,
    field,
  }) => (
    <button
      type="button"
      disabled={updating}
      onClick={() => updateFlag(field, !user[field])}
      className={`px-3 py-1 rounded text-xs font-medium disabled:opacity-50 ${
        user[field] ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {label}: {user[field] ? 'yes' : 'no'}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button
          type="button"
          onClick={() => navigate('/admin/users')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back to users
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-gray-400" /> Account
              </h2>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Name</dt>
                  <dd className="text-gray-900">{user.name || user.username || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Email</dt>
                  <dd className="text-gray-900">
                    {user.email} {user.emailVerified ? '✓' : '(unverified)'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Plan</dt>
                  <dd className="text-gray-900">
                    {user.subscriptionPlan}{' '}
                    {user.subscriptionStatus ? `(${user.subscriptionStatus})` : ''}
                    {user.compedPlan && (
                      <span className="ml-1 px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-800">
                        complimentary {user.compedPlan}
                      </span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Credits</dt>
                  <dd className="text-gray-900">⭐ {user.tokenBalance}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Joined</dt>
                  <dd className="text-gray-900">{new Date(user.createdAt).toLocaleDateString()}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Last login</dt>
                  <dd className="text-gray-900">
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
                  </dd>
                </div>
              </dl>
              <div className="flex flex-wrap gap-2 mt-4">
                <Toggle label="Active" field="isActive" />
                <Toggle label="Admin" field="isAdmin" />
                <Toggle label="Test user" field="isTestUser" />
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  disabled={updating}
                  onClick={() => setComp(!user.compedPlan)}
                  className="px-3 py-1 rounded text-xs font-medium bg-purple-50 text-purple-800 hover:bg-purple-100 disabled:opacity-50"
                >
                  {user.compedPlan ? 'End complimentary plan' : 'Give complimentary Plus'}
                </button>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Usage</h2>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-gray-500">Stories</dt>
                  <dd className="font-medium">{stats.storiesCount}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Words written</dt>
                  <dd className="font-medium">{stats.wordsWritten.toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">AI calls</dt>
                  <dd className="font-medium">{stats.aiCalls}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Tokens used</dt>
                  <dd className="font-medium">{stats.totalTokensUsed.toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Payments</dt>
                  <dd className="font-medium">{stats.paymentsCount}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Total spent</dt>
                  <dd className="font-medium">${stats.totalSpent.toFixed(2)}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <BookOpenIcon className="w-5 h-5 text-gray-400" /> Stories ({stories.length})
              </h2>
              {stories.length === 0 ? (
                <p className="text-sm text-gray-500">No stories yet</p>
              ) : (
                <div className="divide-y divide-gray-200">
                  {stories.map((story) => (
                    <div key={story.id} className="py-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{story.title}</div>
                        <div className="text-xs text-gray-500">
                          {story.format.replace('_', ' ')} · {story.status} ·{' '}
                          {story.wordCount.toLocaleString()} words
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(story.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <CpuChipIcon className="w-5 h-5 text-gray-400" /> Recent AI usage
              </h2>
              {recentAiUsage.length === 0 ? (
                <p className="text-sm text-gray-500">No AI usage yet</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="py-2 pr-4">When</th>
                      <th className="py-2 pr-4">Model</th>
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4 text-right">Tokens</th>
                      <th className="py-2 text-right">Charged</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentAiUsage.map((u) => (
                      <tr key={u.id}>
                        <td className="py-2 pr-4 text-gray-500">
                          {new Date(u.createdAt).toLocaleString()}
                        </td>
                        <td className="py-2 pr-4">{u.model}</td>
                        <td className="py-2 pr-4">{u.type}</td>
                        <td className="py-2 pr-4 text-right">{u.totalTokens.toLocaleString()}</td>
                        <td className="py-2 text-right">${Number(u.chargedAmount).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetail;
